import { useState, useMemo, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { VendorScoreBadge } from '@/components/VendorScorecard';
import {
  Search, X, Zap, Clock, IndianRupee, Sparkles, ArrowUpDown,
  ArrowUp, ArrowDown, AlertTriangle, Scissors, Filter,
} from 'lucide-react';

const DELIVERY_FILTERS = [
  { key: 'now', label: 'Available Now', maxDays: 2 },
  { key: '1week', label: 'Within 1 Week', maxDays: 7 },
  { key: '2weeks', label: 'Within 2 Weeks', maxDays: 14 },
  { key: '2plus', label: '2+ Weeks', minDays: 15 },
];

const PRICE_SEGMENTS = [
  { key: 'under1k', label: 'Under ₹1,000/m', max: 1000 },
  { key: '1k-2.5k', label: '₹1,000-2,500/m', min: 1000, max: 2500 },
  { key: '2.5k-5k', label: '₹2,500-5,000/m', min: 2500, max: 5000 },
  { key: '5kplus', label: '₹5,000+/m', min: 5000 },
];

const TIER_LABELS = { standard: 'Standard', premium: 'Premium', luxury: 'Luxury' };

const EST_PRODUCTION_DAYS = 14;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return '';
  return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
};

export default function FabricPicker({
  fabrics, onSelect, cs = '₹',
  requiredByDate, clientBudgetTier,
}) {
  const [search, setSearch] = useState('');
  const [deliveryFilter, setDeliveryFilter] = useState(null);
  const [priceSegment, setPriceSegment] = useState(null);
  const [tierFilter, setTierFilter] = useState(null);
  const [sortPrice, setSortPrice] = useState(null); // 'asc' | 'desc' | null
  const [smartFilter, setSmartFilter] = useState(false);

  // Calculate max allowed lead time from required_by_date
  const maxLeadTime = useMemo(() => {
    if (!requiredByDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const reqDate = new Date(requiredByDate);
    reqDate.setHours(0, 0, 0, 0);
    const totalDays = Math.ceil((reqDate - today) / (1000 * 60 * 60 * 24));
    return Math.max(totalDays - EST_PRODUCTION_DAYS, 0);
  }, [requiredByDate]);

  const getDeliveryStatus = useCallback((leadDays) => {
    if (maxLeadTime === null) return null;
    if (leadDays <= maxLeadTime) return 'ok';
    const delay = leadDays - maxLeadTime;
    if (delay <= 7) return 'warning';
    return 'late';
  }, [maxLeadTime]);

  const activeFilterCount = [
    search, deliveryFilter, priceSegment, tierFilter, sortPrice, smartFilter,
  ].filter(Boolean).length;

  const clearAll = () => {
    setSearch(''); setDeliveryFilter(null); setPriceSegment(null);
    setTierFilter(null); setSortPrice(null); setSmartFilter(false);
  };

  const applySmartFilter = () => {
    setSmartFilter(true);
    // Auto-apply: delivery within timeline + client budget tier
    if (maxLeadTime !== null) {
      if (maxLeadTime <= 2) setDeliveryFilter('now');
      else if (maxLeadTime <= 7) setDeliveryFilter('1week');
      else if (maxLeadTime <= 14) setDeliveryFilter('2weeks');
      else setDeliveryFilter(null);
    }
    if (clientBudgetTier && TIER_LABELS[clientBudgetTier]) {
      setTierFilter(clientBudgetTier);
    }
    setPriceSegment(null);
    setSortPrice('asc');
  };

  const filtered = useMemo(() => {
    let list = [...fabrics];

    // Text search
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(f =>
        (f.internal_code || '').toLowerCase().includes(q) ||
        (f.fabric_name || '').toLowerCase().includes(q) ||
        (f.mill_name || '').toLowerCase().includes(q) ||
        (f.colour_primary || '').toLowerCase().includes(q) ||
        (f.pattern_type || '').toLowerCase().includes(q)
      );
    }

    // Delivery speed filter
    if (deliveryFilter) {
      const df = DELIVERY_FILTERS.find(d => d.key === deliveryFilter);
      if (df) {
        if (df.maxDays !== undefined) {
          list = list.filter(f => (f.lead_time_days || 0) <= df.maxDays);
        }
        if (df.minDays !== undefined) {
          list = list.filter(f => (f.lead_time_days || 0) >= df.minDays);
        }
      }
    }

    // Price segment filter
    if (priceSegment) {
      const ps = PRICE_SEGMENTS.find(p => p.key === priceSegment);
      if (ps) {
        list = list.filter(f => {
          const price = f.price_per_metre || 0;
          if (ps.min !== undefined && price < ps.min) return false;
          if (ps.max !== undefined && price >= ps.max) return false;
          return true;
        });
      }
    }

    // Tier filter
    if (tierFilter) {
      list = list.filter(f => f.price_tier === tierFilter);
    }

    // Sort by price
    if (sortPrice === 'asc') list.sort((a, b) => (a.price_per_metre || 0) - (b.price_per_metre || 0));
    else if (sortPrice === 'desc') list.sort((a, b) => (b.price_per_metre || 0) - (a.price_per_metre || 0));

    return list;
  }, [fabrics, search, deliveryFilter, priceSegment, tierFilter, sortPrice]);

  return (
    <div className="border rounded-xl overflow-hidden" style={{ borderColor: 'rgba(27,42,74,0.12)' }}
      data-testid="fabric-picker">
      {/* Sticky Filter Bar */}
      <div className="sticky top-0 z-10 bg-white border-b space-y-2 p-2.5"
        style={{ borderColor: 'rgba(27,42,74,0.08)' }} data-testid="fabric-filter-bar">

        {/* Search + Smart Filter + Active Count */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search fabrics..." className="h-8 text-xs pl-8 rounded-lg"
              data-testid="fabric-search-input" />
          </div>
          <Button size="sm" variant={smartFilter ? 'default' : 'outline'}
            className="gap-1 text-[10px] h-8 rounded-lg whitespace-nowrap px-2.5"
            style={smartFilter ? { backgroundColor: '#7C3AED' } : {}}
            onClick={smartFilter ? clearAll : applySmartFilter}
            data-testid="smart-filter-btn">
            <Sparkles className="w-3 h-3" />
            {smartFilter ? 'Smart ON' : 'Smart Filter'}
          </Button>
          {activeFilterCount > 0 && (
            <button onClick={clearAll} className="flex items-center gap-1 text-[10px] font-medium whitespace-nowrap hover:underline"
              style={{ color: '#DC2626' }} data-testid="clear-filters-btn">
              <Filter className="w-3 h-3" />
              {activeFilterCount} active
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Delivery Speed */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[9px] font-bold uppercase tracking-wider flex items-center gap-1"
            style={{ color: 'var(--ink-muted)' }}>
            <Clock className="w-3 h-3" /> Delivery
          </span>
          {DELIVERY_FILTERS.map(d => (
            <button key={d.key} onClick={() => setDeliveryFilter(deliveryFilter === d.key ? null : d.key)}
              className="text-[10px] px-2 py-1 rounded-md border font-medium transition-colors"
              style={{
                backgroundColor: deliveryFilter === d.key ? 'rgba(27,42,74,0.06)' : 'transparent',
                borderColor: deliveryFilter === d.key ? 'var(--brand-navy)' : 'rgba(27,42,74,0.1)',
                color: deliveryFilter === d.key ? 'var(--brand-navy)' : 'var(--ink-muted)',
              }}
              data-testid={`delivery-filter-${d.key}`}>
              {d.label}
            </button>
          ))}
        </div>

        {/* Price Segment + Tier + Sort */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[9px] font-bold uppercase tracking-wider flex items-center gap-1"
            style={{ color: 'var(--ink-muted)' }}>
            <IndianRupee className="w-3 h-3" /> Price
          </span>
          {PRICE_SEGMENTS.map(p => (
            <button key={p.key} onClick={() => setPriceSegment(priceSegment === p.key ? null : p.key)}
              className="text-[10px] px-2 py-1 rounded-md border font-medium transition-colors"
              style={{
                backgroundColor: priceSegment === p.key ? 'rgba(27,42,74,0.06)' : 'transparent',
                borderColor: priceSegment === p.key ? 'var(--brand-navy)' : 'rgba(27,42,74,0.1)',
                color: priceSegment === p.key ? 'var(--brand-navy)' : 'var(--ink-muted)',
              }}
              data-testid={`price-filter-${p.key}`}>
              {p.label}
            </button>
          ))}
          <div className="h-4 w-px mx-1" style={{ backgroundColor: 'rgba(27,42,74,0.1)' }} />
          {Object.entries(TIER_LABELS).map(([k, v]) => (
            <button key={k} onClick={() => setTierFilter(tierFilter === k ? null : k)}
              className="text-[10px] px-2 py-1 rounded-md border font-medium transition-colors"
              style={{
                backgroundColor: tierFilter === k ? 'rgba(197,165,90,0.1)' : 'transparent',
                borderColor: tierFilter === k ? 'var(--brand-gold)' : 'rgba(27,42,74,0.1)',
                color: tierFilter === k ? '#92400E' : 'var(--ink-muted)',
              }}
              data-testid={`tier-filter-${k}`}>
              {v}
            </button>
          ))}
          <div className="h-4 w-px mx-1" style={{ backgroundColor: 'rgba(27,42,74,0.1)' }} />
          <button onClick={() => setSortPrice(sortPrice === 'asc' ? 'desc' : sortPrice === 'desc' ? null : 'asc')}
            className="text-[10px] px-2 py-1 rounded-md border font-medium flex items-center gap-1 transition-colors"
            style={{
              backgroundColor: sortPrice ? 'rgba(27,42,74,0.06)' : 'transparent',
              borderColor: sortPrice ? 'var(--brand-navy)' : 'rgba(27,42,74,0.1)',
              color: sortPrice ? 'var(--brand-navy)' : 'var(--ink-muted)',
            }}
            data-testid="sort-price-btn">
            {sortPrice === 'asc' ? <ArrowUp className="w-3 h-3" /> : sortPrice === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUpDown className="w-3 h-3" />}
            Sort Price
          </button>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between">
          <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
            {filtered.length} fabric{filtered.length !== 1 ? 's' : ''} found
          </span>
          {maxLeadTime !== null && (
            <span className="text-[10px] flex items-center gap-1" style={{ color: '#059669' }}>
              <Clock className="w-3 h-3" /> Max lead time: {maxLeadTime} day{maxLeadTime !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Fabric List */}
      <div className="max-h-[300px] overflow-y-auto" data-testid="fabric-results-list">
        {filtered.length === 0 ? (
          <div className="p-6 text-center">
            <Scissors className="w-8 h-8 mx-auto mb-2 opacity-20" />
            <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>No fabrics match your filters</p>
            <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={clearAll}>Clear Filters</Button>
          </div>
        ) : (
          filtered.map(f => {
            const deliveryStatus = getDeliveryStatus(f.lead_time_days || 0);
            const delayDays = maxLeadTime !== null ? Math.max((f.lead_time_days || 0) - maxLeadTime, 0) : 0;

            return (
              <button key={f.id} onClick={() => onSelect(f)}
                className="w-full text-left px-3 py-2.5 hover:bg-[color:var(--surface-2)] flex items-center gap-2.5 border-b last:border-b-0"
                style={{ borderColor: 'rgba(27,42,74,0.04)', transition: 'background 120ms' }}
                data-testid={`fabric-option-${f.id}`}>
                {f.swatch_image_url ? (
                  <img src={resolveUrl(f.swatch_image_url)} alt="" className="w-9 h-9 rounded-md object-cover flex-shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(27,42,74,0.04)' }}>
                    <Scissors className="w-4 h-4 opacity-20" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold truncate" style={{ color: 'var(--navy-ink)' }}>
                      {f.internal_code || f.fabric_name}
                    </span>
                    {f.price_tier && (
                      <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: f.price_tier === 'luxury' ? 'rgba(197,165,90,0.12)' : 'rgba(27,42,74,0.05)',
                          color: f.price_tier === 'luxury' ? '#92400E' : 'var(--ink-muted)',
                        }}>
                        {f.price_tier}
                      </span>
                    )}
                    {/* Delivery warning badges */}
                    {deliveryStatus === 'warning' && (
                      <span className="text-[8px] font-medium px-1.5 py-0.5 rounded flex items-center gap-0.5"
                        style={{ backgroundColor: 'rgba(245,158,11,0.1)', color: '#D97706' }}
                        data-testid={`delivery-warning-${f.id}`}>
                        <AlertTriangle className="w-2.5 h-2.5" /> May delay {delayDays}d
                      </span>
                    )}
                    {deliveryStatus === 'late' && (
                      <span className="text-[8px] font-medium px-1.5 py-0.5 rounded flex items-center gap-0.5"
                        style={{ backgroundColor: 'rgba(239,68,68,0.08)', color: '#DC2626' }}
                        data-testid={`delivery-late-${f.id}`}>
                        <AlertTriangle className="w-2.5 h-2.5" /> Won't meet date (+{delayDays}d)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                      {f.mill_name}
                    </span>
                    <VendorScoreBadge vendorName={f.mill_name} />
                    {f.colour_primary && (
                      <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                        {f.colour_primary}
                      </span>
                    )}
                    {f.lead_time_days > 0 && (
                      <span className="text-[10px] flex items-center gap-0.5" style={{ color: 'var(--ink-muted)' }}>
                        <Clock className="w-2.5 h-2.5" /> {f.lead_time_days}d
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-xs font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                    {cs}{(f.price_per_metre || 0).toLocaleString('en-IN')}
                  </span>
                  <span className="text-[10px] block" style={{ color: 'var(--ink-muted)' }}>/metre</span>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
